import {
  Alert,
  Dialog,
  RadioGroupControl,
  Select,
  TextV2,
} from '@insights-ltd/design-library/components';
import Grid from '@mui/material/Grid';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ShareType,
  TeamLearnerResponse,
  useDownloadTeamProfiles,
  useGetProfileIds,
  useShareProfiles,
} from 'api';
import { useRequestErrorContext } from 'components/RequestErrorDialog/RequestErrorProvider';
import { EmailProgressDialogV2 } from 'components/EmailProgressDialogV2';
import DownloadingProfilesDialog from 'components/DownloadingToS3Dialog/DownloadingToS3Dialog';
import { AllDialectOptions, ProfileDialectStrategies } from 'types/dialects';
import { DIALECT_CODES, IMPACT_CORE_LANGUAGES } from 'variables';
import { getEvaluatorDialects, useFormatEvaluatorDialect } from 'domain/teams';
import { LicensedProduct, ProfileType } from 'types/types';
import isPronounSupportedByDialect from 'utils/isPronounSupportedByDialect';
import { TFunction } from 'i18next';
import { useBetaEnabled } from 'features';

type RadioType = 'share' | 'download';
type ProfileTypeOption = 'discovery' | 'impact';

const PROFILE_TYPE_OPTION_DISCOVERY: ProfileTypeOption = 'discovery';
const PROFILE_TYPE_OPTION_IMPACT: ProfileTypeOption = 'impact';

const PROFILE_TYPE_BY_OPTION: Record<ProfileTypeOption, ProfileType> = {
  [PROFILE_TYPE_OPTION_DISCOVERY]: 'DISCOVERY_PROFILE',
  [PROFILE_TYPE_OPTION_IMPACT]: 'IMPACT_PROFILE',
};

type DialogProps = {
  isCheckProfilesLoading: boolean;
  profileIdsCount: number;
  totalImpactProfiles: number;
  totalSelectedLearners: number;
  shareMethod: ShareType;
  shareOptions: {
    value: ShareType;
    label: string;
    disabled?: boolean;
  }[];
  radioValue: RadioType;
  shareOrDownloadOptions: {
    value: RadioType;
    label: string;
  }[];
  setShareMethod: (value: ShareType) => void;
  setRadioValue: React.Dispatch<React.SetStateAction<RadioType>>;
  setSelectedDialect: (value: AllDialectOptions) => void;
  selectedDialect: AllDialectOptions;
  productType?: LicensedProduct;
  unsupportedLearnersCount?: number;
  profileTypeOption: ProfileTypeOption;
  setProfileTypeOption: React.Dispatch<React.SetStateAction<ProfileTypeOption>>;
};

const DialogContent = ({
  isCheckProfilesLoading,
  profileIdsCount,
  totalImpactProfiles,
  totalSelectedLearners,
  shareMethod,
  shareOptions,
  radioValue,
  shareOrDownloadOptions,
  setShareMethod,
  setRadioValue,
  setSelectedDialect,
  selectedDialect,
  productType = 'DISCOVERY_PERSONAL_PROFILE',
  unsupportedLearnersCount = 0,
  profileTypeOption,
  setProfileTypeOption,
}: DialogProps) => {
  const { t } = useTranslation();
  const impactProfileEnabled = useBetaEnabled('impact-profile-team-page');
  const actionLabel =
    radioValue === 'share'
      ? t(
          'ui.event-management.events.download-or-share-profiles.pereferred-language-label',
        )
      : t(
          'ui.event-management.events.download-or-share-profiles.download.preferred-language-label',
        );

  const evaluatorDialects =
    profileTypeOption === PROFILE_TYPE_OPTION_IMPACT
      ? IMPACT_CORE_LANGUAGES
      : getEvaluatorDialects(productType);

  const dialectOptions = useFormatEvaluatorDialect(evaluatorDialects);

  const infoMessage =
    profileTypeOption === PROFILE_TYPE_OPTION_IMPACT
      ? t(
          'ui.event-management.events.download-or-share-profiles-modal.impact-info-message',
        )
      : t(
          'ui.event-management.events.download-or-share-profiles-modal.discovery-info-message',
        );

  const selectActionKey = impactProfileEnabled
    ? 'ui.event-management.events.download-or-share-profiles-modal.select-action'
    : 'ui.event-management.events.download-or-share-profiles-modal.found-learners';

  if (isCheckProfilesLoading) return null;
  return (
    <Grid
      container
      sx={{
        justifyContent: 'center',
        alignContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginLeft: '-4px',
      }}
      spacing={1}
    >
      {impactProfileEnabled && (
        <>
          <Grid>
            <TextV2 component="span" color="textSecondary">
              <Trans
                i18nKey="ui.event-management.events.download-or-share-profiles-modal.select-profile-type"
                components={{
                  bold: <TextV2 component="span" variant="bodyBold" />,
                }}
                values={{
                  count: totalSelectedLearners,
                }}
              />
            </TextV2>
          </Grid>
          <Grid>
            <RadioGroupControl
              data-testid="impact-or-discovery-option"
              name="DiscoveryOrImpact"
              title={t(
                'ui.event-management.events.download-or-share-profiles-modal.radio.type.aria-label',
              )}
              onChange={(value) => setProfileTypeOption(value)}
              value={profileTypeOption}
              options={[
                {
                  value: PROFILE_TYPE_OPTION_DISCOVERY,
                  label: t(
                    'ui.event-management.events.download-or-share-profiles-modal.radio.type.product-with-count',
                    {
                      product: t(
                        'ui.event-management.events.download-or-share-profiles-modal.radio.type.insights-discovery',
                      ),
                      count: profileIdsCount,
                    },
                  ),
                  disabled: profileIdsCount === 0,
                },
                {
                  value: PROFILE_TYPE_OPTION_IMPACT,
                  label: t(
                    'ui.event-management.events.download-or-share-profiles-modal.radio.type.product-with-count',
                    {
                      product: t(
                        'ui.event-management.events.download-or-share-profiles-modal.radio.type.insights-impact',
                      ),
                      count: totalImpactProfiles,
                    },
                  ),
                  disabled: totalImpactProfiles === 0,
                },
              ]}
            />
          </Grid>
        </>
      )}
      <Grid>
        <TextV2 component="span">
          <Trans
            i18nKey={selectActionKey}
            components={{
              bold: <TextV2 component="span" variant="bodyBold" />,
            }}
            values={{
              count: profileIdsCount,
            }}
          />
        </TextV2>
      </Grid>
      <Grid>
        <RadioGroupControl
          data-testid="share-or-download-option"
          name="ShareOrDownloadOptions"
          title="Share or download options"
          onChange={(value) => setRadioValue(value)}
          value={radioValue}
          options={shareOrDownloadOptions}
        />
      </Grid>
      <Grid>
        <TextV2 component="span" variant="bodyBold">
          {actionLabel}
        </TextV2>
        <Select
          id="download-share-language-select"
          items={{
            ...dialectOptions,
          }}
          fullWidth
          value={selectedDialect}
          disabled={radioValue === 'share'}
          onChange={(value) =>
            setSelectedDialect(value.target.value as AllDialectOptions)
          }
        />
      </Grid>

      {unsupportedLearnersCount > 0 && radioValue === 'download' && (
        <Grid>
          <Alert severity="warning">
            {t(
              'ui.event-management.events.download-or-share-profiles-modal.warning-message.unsupported',
              {
                unsupportedCount: unsupportedLearnersCount,
                totalSelected: profileIdsCount,
              },
            )}
          </Alert>
        </Grid>
      )}

      {radioValue === 'share' && (
        <>
          <Grid>
            <Alert>{infoMessage}</Alert>
          </Grid>
          <Grid>
            <RadioGroupControl
              name="radio-share-method"
              title={t(
                'ui.event-management.events.download-or-share-profiles-modal.radio.share.aria-label',
              )}
              onChange={(value) => setShareMethod(value)}
              value={shareMethod}
              options={shareOptions}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export const DownloadShareModal = ({
  isOpen,
  setIsOpen,
  teamId,
  learnerIds,
  setCheckedItems,
  learners,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  teamId: string;
  learnerIds: string[];
  learners: { [key: string]: { learnerId: string } & TeamLearnerResponse };
  setCheckedItems?: (value: any) => void;
}) => {
  const { t } = useTranslation();
  const impactProfileEnabled = useBetaEnabled('impact-profile-team-page');
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [radioValue, setRadioValue] = useState<RadioType>('share');
  const [profileTypeOption, setProfileTypeOption] = useState<ProfileTypeOption>(
    PROFILE_TYPE_OPTION_DISCOVERY,
  );
  const [selectedDialect, setSelectedDialect] =
    useState<AllDialectOptions>('evaluation-dialect');
  const [totalDiscoveryProfiles, setTotalDiscoveryProfiles] = useState(0);
  const [totalImpactProfiles, setTotalImpactProfiles] = useState(0);
  const isPreferredDialect = selectedDialect === 'evaluation-dialect';
  const selectedLearners = Object.entries(learners).filter(([, learner]) => {
    return learnerIds.includes(learner.learnerId);
  });
  const supportedLearners = selectedLearners.filter(
    ([, { pronoun, preferredDialect }]) => {
      const dialect = isPreferredDialect ? preferredDialect : selectedDialect;
      return isPronounSupportedByDialect({
        pronoun: pronoun,
        dialect,
      });
    },
  );

  const supportedProfileIds = supportedLearners.flatMap(([, learner]) =>
    learner?.learnerId ? [learner.learnerId] : [],
  );

  const {
    isPending: isCheckDiscoveryProfilesPending,
    data: profileIdsData = { profileIds: [] },
    isError: isCheckDiscoveryProfilesError,
  } = useGetProfileIds(teamId, 'DISCOVERY_PROFILE', supportedProfileIds, {
    enabled: isOpen,
  });
  useEffect(() => {
    if (isPreferredDialect && profileIdsData?.profileIds?.length > 0) {
      setTotalDiscoveryProfiles(profileIdsData.profileIds.length);
    }
  }, [profileIdsData?.profileIds?.length, isPreferredDialect]);

  const {
    isPending: isCheckImpactProfilesPending,
    data: impactProfileIdsData = { profileIds: [] },
    isError: isCheckImpactProfilesError,
  } = useGetProfileIds(teamId, 'IMPACT_PROFILE', supportedProfileIds, {
    enabled: isOpen && impactProfileEnabled,
  });
  useEffect(() => {
    if (isPreferredDialect && impactProfileIdsData?.profileIds?.length > 0) {
      setTotalImpactProfiles(impactProfileIdsData.profileIds.length);
    }
  }, [impactProfileIdsData?.profileIds?.length, isPreferredDialect]);

  const isCheckProfilesPending =
    isCheckDiscoveryProfilesPending ||
    (impactProfileEnabled && isCheckImpactProfilesPending);
  const isCheckProfilesError =
    isCheckDiscoveryProfilesError ||
    (impactProfileEnabled && isCheckImpactProfilesError);

  useEffect(() => {
    if (radioValue === 'share') {
      setSelectedDialect('evaluation-dialect');
    }
  }, [radioValue]);

  const { isError: isShareError, mutate: shareProfiles } = useShareProfiles();
  const { isError: isDownloadError, mutate: downloadProfiles } =
    useDownloadTeamProfiles();
  const { openErrorModal } = useRequestErrorContext();

  useEffect(() => {
    if (isCheckProfilesError || isShareError || isDownloadError) {
      setIsOpen(false);
      openErrorModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckProfilesError, isShareError, isDownloadError]);

  const shareOrDownloadOptions = getShareOrDownloadOptions(t);

  const selectedProfileCount =
    profileTypeOption === PROFILE_TYPE_OPTION_IMPACT
      ? totalImpactProfiles
      : totalDiscoveryProfiles;

  const shareOptions = useMemo(
    () => [
      ...getDigitalProfileShareOption(selectedProfileCount, t),
      {
        value: 'link' as ShareType,
        label: t(
          'ui.event-management.events.download-or-share-profiles-modal.radio.share-download-link',
        ),
        disabled: false,
      },
      {
        value: 'attachment' as ShareType,
        label: t(
          'ui.event-management.events.download-or-share-profiles-modal.radio.share-download-pdf',
        ),
        disabled: false,
      },
    ],
    [selectedProfileCount, t],
  );

  const [shareMethod, setShareMethod] = useState<ShareType>(
    shareOptions[0].value,
  );

  useEffect(() => {
    setRadioValue('share');
    setSelectedDialect('evaluation-dialect');
  }, [profileTypeOption]);

  useEffect(() => {
    const selectedOption = shareOptions.find(
      (option) => option.value === shareMethod,
    );
    const fallbackOption = shareOptions.find((option) => !option.disabled);
    if (
      (!selectedOption || selectedOption.disabled) &&
      fallbackOption &&
      fallbackOption.value !== shareMethod
    ) {
      setShareMethod(fallbackOption.value);
    }
  }, [shareMethod, shareOptions]);

  if (isCheckProfilesPending) return null;

  const activeProfileIdsData =
    profileTypeOption === PROFILE_TYPE_OPTION_IMPACT
      ? impactProfileIdsData
      : profileIdsData;

  const { profileIds } =
    supportedProfileIds.length === 0
      ? { profileIds: [] }
      : (activeProfileIdsData ?? { profileIds: [] });

  const hasLearnersWithIDPP = profileIds?.length > 0;
  const isSelectedShareMethodDisabled =
    radioValue === 'share' &&
    shareOptions.some(
      (option) => option.value === shareMethod && option.disabled,
    );
  const canSubmit = hasLearnersWithIDPP && !isSelectedShareMethodDisabled;

  const closeAndClearSelection = () => {
    setIsOpen(false);
    if (setCheckedItems) {
      setCheckedItems({});
    }
  };

  const confirmButtonText =
    radioValue === 'share'
      ? t(
          'ui.event-management.events.download-or-share-profiles-modal.button.share-profile',
          { count: selectedProfileCount },
        )
      : t(
          'ui.event-management.events.download-or-share-profiles-modal.button.download-profile',
          { count: selectedProfileCount },
        );

  function generateUsing() {
    return (
      selectedDialect === DIALECT_CODES.EVALUATION_DIALECT
        ? 'EVALUATION_DIALECT'
        : 'SINGLE_DIALECT'
    ) as ProfileDialectStrategies;
  }

  const unsupportedLearnersCount = learnerIds.length - supportedLearners.length;
  return (
    <>
      <Dialog
        open={isOpen}
        title={t(
          impactProfileEnabled
            ? 'ui.event-management.events.download-or-share-profiles-impact-enabled'
            : 'ui.event-management.events.download-or-share-profiles',
        )}
        content={
          <DialogContent
            isCheckProfilesLoading={isCheckProfilesError}
            profileIdsCount={totalDiscoveryProfiles}
            totalImpactProfiles={totalImpactProfiles}
            totalSelectedLearners={learnerIds.length}
            shareMethod={shareMethod}
            shareOptions={shareOptions}
            radioValue={radioValue}
            shareOrDownloadOptions={shareOrDownloadOptions}
            setShareMethod={setShareMethod}
            setRadioValue={setRadioValue}
            setSelectedDialect={setSelectedDialect}
            selectedDialect={selectedDialect}
            unsupportedLearnersCount={
              unsupportedLearnersCount > 0 ? unsupportedLearnersCount : 0
            }
            profileTypeOption={profileTypeOption}
            setProfileTypeOption={setProfileTypeOption}
          />
        }
        onClose={closeAndClearSelection}
        disablePrimaryButton={!canSubmit}
        primaryButtonText={confirmButtonText}
        secondaryButtonText={t('ui.event-management.cancel')}
        onSubmit={() => {
          if (canSubmit) {
            if (isImpactPdfAction(profileTypeOption, radioValue, shareMethod)) {
              closeAndClearSelection();
              return;
            }
            const body = isPreferredDialect
              ? { profileIds }
              : {
                  profileIds,
                  dialect: selectedDialect,
                  generateUsing: generateUsing(),
                };
            if (radioValue === 'download') {
              downloadProfiles(
                { teamId, body },
                {
                  onSuccess: () => {
                    setOpenDownloadDialog(true);
                    setIsOpen(false);
                  },
                },
              );
              return;
            }

            const shareBody = {
              ...body,
              profileType: PROFILE_TYPE_BY_OPTION[profileTypeOption],
            };

            shareProfiles(
              { teamId, body: shareBody, shareType: shareMethod },
              {
                onSuccess: () => {
                  setOpenEmailDialog(true);
                  setIsOpen(false);
                },
              },
            );
          }
        }}
      />
      <EmailProgressDialogV2
        open={openEmailDialog}
        onClose={() => {
          setOpenEmailDialog(false);
          setIsOpen(false);
        }}
        title={t(
          'ui.event-management.events.download-or-share-profiles.confirmation-modal',
        )}
        header={t(
          'ui.event-management.events.download-or-share-profiles.confirmation-modal.header',
        )}
        body={t(
          'ui.event-management.events.download-or-share-profiles.confirmation-modal.body',
        )}
      />
      <DownloadingProfilesDialog
        open={openDownloadDialog}
        onClose={() => {
          setOpenDownloadDialog(false);
          setIsOpen(false);
          if (setCheckedItems) {
            setCheckedItems({});
          }
        }}
      />
    </>
  );
};

/**
 * Impact profile PDFs can't be generated yet, so downloading or sharing them as a link or attachment does nothing.
 */
function isImpactPdfAction(
  profileTypeOption: ProfileTypeOption,
  radioValue: RadioType,
  shareMethod: ShareType,
) {
  return (
    profileTypeOption === PROFILE_TYPE_OPTION_IMPACT &&
    (radioValue === 'download' || shareMethod !== 'share-digital-profile-link')
  );
}

function getShareOrDownloadOptions(
  t: TFunction,
): { value: RadioType; label: string }[] {
  return [
    {
      value: 'share' as RadioType,
      label: t(
        'ui.event-management.events.download-or-share-profiles-modal.radio.share-or-download',
      ),
    },
    {
      value: 'download' as RadioType,
      label: t(
        'ui.event-management.events.download-or-share-profiles-modal.radio.download-latest-profiles',
      ),
    },
  ];
}

/**
 * Limit the number of emails we send for digital profiles. See commit messgae for more details.
 * Above the limit the option is greyed out rather than removed, so it remains visible even though it can't be selected.
 */
function getDigitalProfileShareOption(profileIdCount: number, t: TFunction) {
  return [
    {
      value: 'share-digital-profile-link' as ShareType,
      label: t(
        'ui.event-management.events.download-or-share-profiles-modal.radio.share-digital-profile-link',
      ),
      disabled: profileIdCount > 200,
    },
  ];
}
